import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Ranking, Medal, Certificate, Crown, Trophy } from "@phosphor-icons/react";
import { Switch } from "@/components/ui/switch";
import { useTranslation } from "@/contexts/LanguageContext";
import { useQuizBadges } from "@/contexts/QuizBadgesContext";
import { useAuth } from "@/contexts/AuthContext";

const RANK_COLORS = ["text-[#F5A524]", "text-[#D4D4D8]", "text-[#C2793A]"];

export default function Leaderboard() {
  const { lang } = useTranslation();
  const es = lang === "es";
  const { badges } = useQuizBadges();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () =>
    axios.get("/api/leaderboard", { withCredentials: true }).then((r) => setData(r.data)).catch(() => setData({ top: [], me: null }));

  useEffect(() => {
    load();
  }, [badges.size]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = async (opt_in) => {
    setSaving(true);
    try {
      await axios.post("/api/leaderboard/opt-in", { opt_in }, { withCredentials: true });
      await load();
      toast.success(opt_in ? (es ? "Ahora apareces en la tabla" : "You're now on the leaderboard") : es ? "Ya no apareces en la tabla" : "You're off the leaderboard");
    } catch {
      toast.error(es ? "No se pudo guardar" : "Couldn't save");
    }
    setSaving(false);
  };

  if (!data) return null;
  const { top, me } = data;

  return (
    <div className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-4 sm:p-5 space-y-4" data-testid="leaderboard">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="flex items-center gap-2 font-['Outfit'] text-white text-lg font-semibold">
            <Ranking weight="fill" className="w-5 h-5 text-[#F5A524]" />
            {es ? "Tabla de la Clase" : "Class Leaderboard"}
          </h2>
          <p className="text-[#71717A] text-sm">
            {es ? "Top 10 por insignias; el mejor puntaje del examen desempata. Solo aparecen quienes lo activan." : "Top 10 by badges; best exam score breaks ties. Only students who opt in appear."}
          </p>
        </div>
        {me && user?.role !== "admin" && (
          <label className="flex items-center gap-2.5 bg-[#0A0A0A] border border-white/10 rounded-lg px-3 py-2 cursor-pointer" data-testid="leaderboard-optin">
            <Switch checked={me.opt_in} disabled={saving} onCheckedChange={toggle} data-testid="leaderboard-optin-switch" />
            <span className="text-sm text-[#D4D4D8]">
              {es ? `Mostrarme como "${me.display}"` : `Show me as "${me.display}"`}
            </span>
          </label>
        )}
      </div>

      {top.length === 0 ? (
        <p className="text-[#71717A] text-sm" data-testid="leaderboard-empty">
          {es ? "Aún no hay nadie en la tabla. ¡Activa la opción y gana tu primera insignia!" : "No one on the board yet. Opt in and earn your first badge!"}
        </p>
      ) : (
        <ol className="divide-y divide-white/5" data-testid="leaderboard-list">
          {top.map((r, i) => (
            <li
              key={`${r.display}-${i}`}
              className={`flex items-center gap-3 py-2.5 px-2 rounded-lg ${r.is_me ? "bg-[#F5A524]/10" : ""}`}
              data-testid={`leaderboard-row-${i}`}
            >
              <span className={`w-7 text-center font-['JetBrains_Mono'] font-semibold ${RANK_COLORS[i] || "text-[#71717A]"}`}>
                {i === 0 ? <Crown weight="fill" className="w-5 h-5 inline" /> : i + 1}
              </span>
              <span className="flex-1 min-w-0 truncate text-white text-sm">
                {r.display}
                {r.champion > 0 && (
                  <Trophy weight="fill" className="w-4 h-4 text-[#F5A524] inline ml-1.5 -mt-0.5" data-testid={`leaderboard-champion-${i}`} />
                )}
                {r.is_me && <span className="text-[#F5A524] text-xs ml-2">{es ? "(tú)" : "(you)"}</span>}
              </span>
              <span className="flex items-center gap-1 text-sm text-emerald-300 font-['JetBrains_Mono']" title={es ? "Insignias" : "Badges"}>
                <Medal weight="fill" className="w-4 h-4" />
                {r.badges}/{data.badges_total}
              </span>
              <span className="flex items-center gap-1 text-sm text-[#F5A524] font-['JetBrains_Mono'] min-w-[4.5rem] justify-end" title={es ? "Mejor examen" : "Best exam"}>
                <Certificate weight="fill" className="w-4 h-4" />
                {r.best_exam ? `${r.best_exam}/${data.exam_total}` : "-"}
              </span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
